import { useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, CalendarCheck, Phone, MessageCircle } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { IMAGES, CONTACTS } from "./data";

const LINES = ["Noleggio e assistenza", "multifunzione,", "senza pensieri."];

const BOOKING_WA = `https://wa.me/${CONTACTS.mobileRaw.replace("+", "")}?text=${encodeURIComponent("Vorrei prendere appuntamento")}`;

const BookingButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="booking-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 bg-[#C85A32] text-[#FBF9F5] px-7 py-3.5 rounded-full text-sm hover:bg-[#181817] transition-colors duration-300"
      >
        <CalendarCheck size={15} />
        Prenota un Appuntamento
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              data-testid="booking-popover"
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-full mt-3 z-50 w-80 max-w-[85vw] bg-[#FBF9F5] border border-[#E5E2DC] rounded-sm shadow-[0_30px_60px_-20px_rgba(24,24,23,0.3)] p-3"
            >
              <p className="px-3 pt-2 pb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">
                Scegli come contattarci
              </p>
              <a
                data-testid="booking-call-option"
                href={`tel:${CONTACTS.phoneRaw}`}
                className="flex items-center gap-4 p-4 rounded-sm hover:bg-[#EBE6DD] transition-colors duration-300"
              >
                <span className="w-11 h-11 shrink-0 rounded-full bg-[#181817] text-[#FBF9F5] flex items-center justify-center">
                  <Phone size={17} />
                </span>
                <span>
                  <span className="block text-sm text-[#181817]">Chiama ora</span>
                  <span className="block font-mono text-xs text-[#6E6D69] mt-0.5">{CONTACTS.phone}</span>
                </span>
              </a>
              <a
                data-testid="booking-whatsapp-option"
                href={BOOKING_WA}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-sm hover:bg-[#EBE6DD] transition-colors duration-300"
              >
                <span className="w-11 h-11 shrink-0 rounded-full bg-[#25D366] text-white flex items-center justify-center">
                  <MessageCircle size={17} />
                </span>
                <span>
                  <span className="block text-sm text-[#181817]">Scrivi su WhatsApp</span>
                  <span className="block font-mono text-xs text-[#6E6D69] mt-0.5">{CONTACTS.mobile}</span>
                </span>
              </a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const TiltCard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 140, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      ref={ref}
      data-testid="hero-visual-card"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      style={{ rotateX, rotateY, y, transformStyle: "preserve-3d", perspective: 900 }}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      <div className="overflow-hidden rounded-sm border border-[#E5E2DC] shadow-[0_40px_80px_-30px_rgba(24,24,23,0.25)]">
        <img src={IMAGES.heroOffice} alt="La sede Furlan Print Copy a Paderno Dugnano" className="w-full aspect-[4/5] object-cover" />
      </div>
      <div className="absolute -bottom-6 -left-6 bg-[#181817] text-[#FBF9F5] px-6 py-4 rounded-sm" style={{ transform: "translateZ(50px)" }}>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C85A32]">Assistenza rapida</p>
        <p className="font-display text-lg font-light">Intervento entro 24h</p>
      </div>
    </motion.div>
  );
};

export const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 lg:gap-20 items-center">
      <div>
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-[#6E6D69] mb-8"
        >
          Noleggio & assistenza multifunzione — Paderno Dugnano
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-[#181817]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? <em className="not-italic text-[#C85A32]">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 text-lg sm:text-xl text-[#6E6D69] leading-relaxed font-light max-w-xl"
        >
          Tutta la gamma Canon imageRUNNER a noleggio, con toner e assistenza inclusi. Da Paderno Dugnano serviamo uffici, aziende e privati in tutta Milano e provincia.
        </motion.p>
        <motion.p
          data-testid="hero-b2b-note"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#C85A32]"
        >
          + Vendita di PC e periferiche per la postazione di lavoro
        </motion.p>
        <motion.p
          data-testid="booking-notice"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]"
        >
          Si prega di prendere appuntamento prima di passare a trovarci
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-4 flex flex-wrap items-center gap-4 sm:gap-6"
        >
          <BookingButton />
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToSection("#lavori")}
            className="group flex items-center gap-3 bg-[#181817] text-[#FBF9F5] px-7 py-3.5 rounded-full text-sm hover:bg-[#C85A32] transition-colors duration-300"
          >
            Scopri le Soluzioni
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <div className="hidden sm:flex flex-col items-center gap-1" data-testid="hero-scroll-indicator">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6E6D69]">Scorri</span>
            <div className="w-px h-8 bg-[#C5C2BA] relative overflow-hidden">
              <span className="absolute top-0 left-0 w-px h-2 bg-[#C85A32] animate-scroll-dot" />
            </div>
          </div>
        </motion.div>
      </div>
      <TiltCard />
    </div>
  </section>
);
